import React from "react";
import { Button } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  removeCartItem,
  updateCartItem,
  getCart,
  removeFromGuestCart,
  updateGuestCartItemQuantity
} from "../../../Redux/Customers/Cart/Action";
import { IconButton, Chip, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  getBoxSizeDisplay,
  calculateBoxPrice,
  BOX_WEIGHTS
} from "../../../utils/boxPricing";

const CartItem = ({ item, showButton, isGuest = false }) => {
  const dispatch = useDispatch();
  const jwt = localStorage.getItem("jwt");

  // Calculate using the same logic as PDP and Cart Summary
  const weight = BOX_WEIGHTS[item?.size] || 5;
  const originalPricePerPound = item?.product?.price || item?.pricePerLb || 0;
  const discountedPricePerPound = item?.product?.discountedPrice || item?.discountedPricePerLb || originalPricePerPound;

  // Original total (strikethrough): original price per pound * weight * quantity
  const originalTotalPrice = calculateBoxPrice(weight, originalPricePerPound) * item?.quantity;
  // Discounted total: discounted price per pound * weight * quantity
  const totalPrice = calculateBoxPrice(weight, discountedPricePerPound) * item?.quantity;
  // Discount amount
  const discountAmount = originalTotalPrice - totalPrice;

  // Check if product is out of stock
  const productQuantity = item?.product?.quantity || 0;
  const isOutOfStock = productQuantity === 0;

  const handleRemoveItemFromCart = async () => {
    if (isGuest) {
      // Guest mode: Remove from sessionStorage
      await dispatch(removeFromGuestCart(item?.id));
    } else {
      // Authenticated mode: Remove from backend
      const data = { cartItemId: item?.id, jwt };
      await dispatch(removeCartItem(data));
      dispatch(getCart(jwt));
    }
  };

  const handleUpdateCartItem = async (num) => {
    const newQuantity = item.quantity + num;

    if (newQuantity < 1) {
      // If quantity would become 0, remove the item
      return handleRemoveItemFromCart();
    }

    if (isGuest) {
      // Guest mode: Update in sessionStorage
      await dispatch(updateGuestCartItemQuantity(item?.id, newQuantity));
    } else {
      // Authenticated mode: Update in backend
      const data = { data: { quantity: newQuantity }, cartItemId: item?.id, jwt };
      await dispatch(updateCartItem(data));
      dispatch(getCart(jwt));
    }
  };

  // Get image URL - handle both guest and authenticated item structures
  const imageUrl = item?.product?.imageUrl || item?.imageUrl;
  const title = item?.product?.title || item?.title;
  const brand = item?.product?.brand || item?.brand;
  const discountPercent = item?.product?.discountPersent || item?.discountPersent || 0;

  return (
    <div className="p-5 shadow-lg border rounded-md">
      <div className="flex items-start">
        <div className="w-[5rem] h-[5rem] lg:w-[9rem] lg:h-[9rem] flex-shrink-0 relative overflow-visible">
          <img
            className="w-full h-full object-cover object-top rounded"
            src={imageUrl}
            alt={title}
          />
          {/* Quantity badge on image — visible in both cart and summary views */}
          {item?.quantity >= 1 && (
            <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-md">
              {item.quantity}
            </span>
          )}
        </div>
        <div className="ml-5 flex-1">
          <div className="space-y-2">
            <p className="font-semibold text-lg">{title}</p>
            <div className="flex items-center flex-wrap gap-2">
              <p className="opacity-70 text-sm">Size: {getBoxSizeDisplay(item?.size)}</p>
              <p className="opacity-70 text-sm">| Qty: {item?.quantity}</p>
              {discountPercent > 0 && (
                <Chip
                  label={`${discountPercent}% Off`}
                  color="success"
                  size="small"
                />
              )}
              {isOutOfStock && (
                <Chip
                  label="Out of Stock"
                  color="error"
                  size="small"
                />
              )}
            </div>
            <p className="opacity-70 text-sm">Seller: {brand}</p>

            {/* Box-based pricing display - Matches PDP and Cart Summary exactly */}
            <div className="space-y-2 pt-3 border-t border-gray-100">
              <div className="flex items-baseline justify-between">
                <div>
                  <Typography variant="h6" className="font-semibold text-gray-900">
                    {discountAmount > 0 && (
                      <span className="opacity-50 line-through mr-2">
                        ${originalTotalPrice.toFixed(2)}
                      </span>
                    )}
                    ${totalPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" className="text-gray-600">
                    {getBoxSizeDisplay(item?.size)} - Line Item Total
                  </Typography>
                  {discountAmount > 0 && (
                    <Typography variant="body2" className="text-green-700 font-medium">
                      You save: ${discountAmount.toFixed(2)}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showButton && (
        <div className="flex flex-col lg:flex-row items-start lg:items-center lg:space-x-10 pt-4 border-t border-gray-100 mt-4">
          <div className="flex items-center space-x-3 py-2">
            <IconButton
              onClick={() => handleUpdateCartItem(-1)}
              disabled={item?.quantity <= 1 || isOutOfStock}
              color="primary"
              aria-label="decrease quantity"
            >
              <RemoveCircleOutlineIcon />
            </IconButton>
            <span className="py-1 px-4 border rounded-md text-lg font-medium bg-gray-50 min-w-[3rem] text-center">
              {item?.quantity}
            </span>
            <IconButton
              onClick={() => handleUpdateCartItem(1)}
              disabled={isOutOfStock || item?.quantity >= 10}
              color="primary"
              aria-label="increase quantity"
              title={item?.quantity >= 10 ? "Bulk limit reached (10 boxes)" : ""}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </div>
          <div className="flex text-sm lg:text-base mt-3 lg:mt-0">
            <Button onClick={handleRemoveItemFromCart} variant="text" color="error">
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItem;
